import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useInvoiceStore } from "@/features/invoices/store/invoice-store";
import { ApiError } from "@/lib/api";

interface ConfirmInvoiceDialogProps {
  invoiceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Whether this client has unapplied credit worth offering to apply. */
  hasOpenCredit: boolean;
}

export function ConfirmInvoiceDialog({
  invoiceId,
  open,
  onOpenChange,
  hasOpenCredit,
}: ConfirmInvoiceDialogProps) {
  const confirmInvoice = useInvoiceStore((s) => s.confirmInvoice);
  const [applyCredit, setApplyCredit] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleConfirm() {
    setSubmitting(true);
    try {
      await confirmInvoice(invoiceId, {
        applyCredit,
        invoiceNumber: invoiceNumber.trim() || undefined,
      });
      toast.success("Invoice confirmed.");
      onOpenChange(false);
    } catch (err) {
      // Surface server validation (e.g. duplicate/invalid override number).
      toast.error(err instanceof ApiError ? err.message : "Couldn't confirm the invoice.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Confirm Invoice</DialogTitle>
          <DialogDescription>
            Confirming assigns the invoice number and opens it. Once confirmed, an invoice is
            read-only (void it to cancel).
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="invoiceNumberOverride">Invoice Number (optional)</Label>
            <Input
              id="invoiceNumberOverride"
              placeholder="Auto-assigned if blank"
              maxLength={12}
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              Up to 12 characters: letters, numbers, space . / # -
            </p>
          </div>
          {hasOpenCredit && (
            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={applyCredit}
                onCheckedChange={(c) => setApplyCredit(c === true)}
                className="mt-0.5"
              />
              <span>
                Apply this client&apos;s available credit to the invoice on confirm (oldest first).
              </span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={submitting}>
            {submitting ? "Confirming…" : "Confirm Invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
